import { TournamentStatus, TournamentType } from '@/src/domain/models';
import { useTranslation } from '@/src/i18n';
import { SortOption } from '@/src/presentation/hooks';
import { Modal, StyleSheet, Text, TouchableOpacity, View } from 'react-native';

export interface FiltersModalProps {
  visible: boolean;
  onClose: () => void;
  filterStatus: TournamentStatus | 'all';
  setFilterStatus: (value: TournamentStatus | 'all') => void;
  filterType: TournamentType | 'all';
  setFilterType: (value: TournamentType | 'all') => void;
  sortBy: SortOption;
  setSortBy: (value: SortOption) => void;
  colors: {
    overlay: string;
    card: string;
    text: string;
    textSecondary: string;
    primary: string;
    primaryText: string;
    surface: string;
    border: string;
  };
}

export const FiltersModal = ({
  visible,
  onClose,
  filterStatus,
  setFilterStatus,
  filterType,
  setFilterType,
  sortBy,
  setSortBy,
  colors,
}: FiltersModalProps) => {
  const { t } = useTranslation();

  const getStatusLabel = (status: TournamentStatus | 'all') => {
    switch (status) {
      case 'all':
        return t('common.all');
      case 'setup':
        return t('tournaments.setup');
      case 'in_progress':
        return t('tournaments.inProgress');
      case 'completed':
        return t('tournaments.completed');
      default:
        return status;
    }
  };

  const getTypeLabel = (type: TournamentType | 'all') => {
    switch (type) {
      case 'all':
        return t('common.all');
      case 'single_elimination':
        return t('filters.single');
      case 'double_elimination':
        return t('filters.double');
      case 'round_robin':
        return t('tournaments.roundRobin');
      default:
        return type;
    }
  };

  const sortOptions = [
    { value: 'date-desc' as const, label: t('filters.newestFirst') },
    { value: 'date-asc' as const, label: t('filters.oldestFirst') },
    { value: 'name-asc' as const, label: t('filters.nameAZ') },
    { value: 'name-desc' as const, label: t('filters.nameZA') },
  ];

  return (
    <Modal visible={visible} animationType="slide" transparent onRequestClose={onClose}>
      <View style={[styles.modalOverlay, { backgroundColor: colors.overlay }]}>
        <View style={[styles.filterModal, { backgroundColor: colors.card }]}>
          <Text style={[styles.filterModalTitle, { color: colors.text }]}>{t('filters.filtersAndSorting')}</Text>

          {/* Status Filter */}
          <Text style={[styles.filterLabel, { color: colors.textSecondary }]}>{t('filters.status')}</Text>
          <View style={styles.filterOptions}>
            {(['all', 'setup', 'in_progress', 'completed'] as const).map((status) => (
              <TouchableOpacity
                key={status}
                style={[
                  styles.filterOption,
                  {
                    backgroundColor: filterStatus === status ? colors.primary : colors.surface,
                    borderColor: colors.border,
                  },
                ]}
                onPress={() => setFilterStatus(status)}
              >
                <Text
                  style={[
                    styles.filterOptionText,
                    { color: filterStatus === status ? colors.primaryText : colors.text },
                  ]}
                >
                  {getStatusLabel(status)}
                </Text>
              </TouchableOpacity>
            ))}
          </View>

          {/* Type Filter */}
          <Text style={[styles.filterLabel, { color: colors.textSecondary }]}>{t('filters.type')}</Text>
          <View style={styles.filterOptions}>
            {(['all', 'single_elimination', 'double_elimination', 'round_robin'] as const).map((type) => (
              <TouchableOpacity
                key={type}
                style={[
                  styles.filterOption,
                  {
                    backgroundColor: filterType === type ? colors.primary : colors.surface,
                    borderColor: colors.border,
                  },
                ]}
                onPress={() => setFilterType(type)}
              >
                <Text
                  style={[styles.filterOptionText, { color: filterType === type ? colors.primaryText : colors.text }]}
                >
                  {getTypeLabel(type)}
                </Text>
              </TouchableOpacity>
            ))}
          </View>

          {/* Sort */}
          <Text style={[styles.filterLabel, { color: colors.textSecondary }]}>{t('filters.sortBy')}</Text>
          <View style={styles.filterOptions}>
            {sortOptions.map((option) => (
              <TouchableOpacity
                key={option.value}
                style={[
                  styles.filterOption,
                  {
                    backgroundColor: sortBy === option.value ? colors.primary : colors.surface,
                    borderColor: colors.border,
                  },
                ]}
                onPress={() => setSortBy(option.value)}
              >
                <Text
                  style={[styles.filterOptionText, { color: sortBy === option.value ? colors.primaryText : colors.text }]}
                >
                  {option.label}
                </Text>
              </TouchableOpacity>
            ))}
          </View>

          <TouchableOpacity style={[styles.filterModalBtn, { backgroundColor: colors.primary }]} onPress={onClose}>
            <Text style={[styles.filterModalBtnText, { color: colors.primaryText }]}>{t('filters.applyFilters')}</Text>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  modalOverlay: {
    flex: 1,
    justifyContent: 'flex-end',
  },
  filterModal: {
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    padding: 20,
    maxHeight: '80%',
  },
  filterModalTitle: {
    fontSize: 22,
    fontWeight: 'bold',
    marginBottom: 20,
  },
  filterLabel: {
    fontSize: 14,
    fontWeight: '600',
    marginTop: 15,
    marginBottom: 10,
    textTransform: 'uppercase',
  },
  filterOptions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
  },
  filterOption: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 20,
    borderWidth: 1,
  },
  filterOptionText: {
    fontSize: 14,
    fontWeight: '500',
  },
  filterModalBtn: {
    marginTop: 25,
    padding: 16,
    borderRadius: 10,
    alignItems: 'center',
  },
  filterModalBtnText: {
    fontSize: 16,
    fontWeight: 'bold',
  },
});
